import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Loader2, User } from "lucide-react";
import { updateCustomers } from "@/services/auth";
import { fetchCustomerData } from "@/redux/slices/customerSlice";
import toast from "react-hot-toast";
import { getT } from "@/i18n/translations";


export default function ProfileSetup() {
  const t = getT();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [villages, setVillages] = useState([]);

  const mobile =
    localStorage.getItem("mobile") ||
    localStorage.getItem("phone") ||
    localStorage.getItem("phoneNumber") || "";

  const [form, setForm] = useState({
    name: "", pin: "", state: "", district: "", village: "", landowned: "",
  });

  const fetchPincodeData = async (pin) => {
    if (pin.length !== 6) return;
    setPincodeLoading(true);
    setVillages([]);
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
      const data = await res.json();
      if (data[0]?.Status === "Success" && data[0]?.PostOffice?.length > 0) {
        const postOffices = data[0].PostOffice;
        const po = postOffices[0];
        const villageNames = [...new Set(postOffices.map((p) => p.Name))];
        setVillages(villageNames);
        setForm((prev) => ({
          ...prev,
          state: po.State || "",
          district: po.District || "",
          village: villageNames[0] || "",
        }));
      } else {
        toast.error(t.profile_invalidPincode);
      }
    } catch {
      toast.error(t.profile_pincodeError);
    }
    setPincodeLoading(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    const val = name === "pin" ? value.replace(/\D/g, "").slice(0, 6) : value;
    setForm((prev) => ({ ...prev, [name]: val }));
    if (name === "pin" && val.length === 6) fetchPincodeData(val);
  };

const handleSubmit = async () => {
  if (!form.name.trim()) {
    toast.error(t.profile_errName);
    return;
  }

  if (!form.pin || form.pin.length !== 6) {
    toast.error(t.profile_errPincode);
    return;
  }

  if (!form.state || !form.district) {
    toast.error(t.profile_errLocation);
    return;
  }

  if (!form.landowned) {
    toast.error(t.profile_errAcreage);
    return;
  }

  setLoading(true);

  try {
    const token = JSON.parse(localStorage.getItem("token_gen"));
    const customerCode = localStorage.getItem("customercode");

    const response = await updateCustomers(
      token?.access_token,
      customerCode,
      {
        firstName: form.name.trim(),
        state: form.state,
        district: form.district,
        village: form.village,
        pin: form.pin,
        landowned: Number(form.landowned),
      }
    );

    if (response?.message === "Request processed successfully.") {
      toast.success(t.profile_saved);

      await dispatch(fetchCustomerData(customerCode));

      localStorage.setItem("isNewUser", "false");

      navigate("/home");
    } else {
      toast.error(t.profile_saveError);
    }
  } catch (error) {
    toast.error(t.profile_saveError);
  } finally {
    setLoading(false);
  }
};

  return (
    <div
   className="min-h-screen flex flex-col items-center"
      style={{
        fontFamily: "'Poppins', sans-serif",
        backgroundColor: '#f0f4f0',
        backgroundImage: 'radial-gradient(circle, #c8d8c8 1px, transparent 1px)',
        backgroundSize: '22px 22px',
      }}
    >
      {loading && (
        <div className="fixed inset-0 bg-black/20 flex justify-center items-center z-50 backdrop-blur-sm">
          <Loader2 className="animate-spin h-10 w-10 text-[#2d5a2d]" />
        </div>
      )}

<div className="w-full max-w-md min-h-screen flex flex-col">
        <div className="flex items-center justify-center px-4 py-4 mt-2">
          <p className="text-[15px] font-semibold text-[#1a2e1a]">{t.profile_heading}</p>
        </div>

    <div className="px-4 pb-8 space-y-3 flex-1">
          <div className="bg-white rounded-2xl px-5 py-5 shadow-sm mx-1 flex items-center gap-4">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center shrink-0"
              style={{ backgroundColor: '#eaf3de' }}
            >
              <User size={24} className="text-[#2d5a2d]" />
            </div>
            <div>
              <p className="text-[13px] font-semibold text-[#1a2e1a]">{mobile}</p>
              <p className="text-[11px] text-gray-400">{t.profile_subheading}</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl px-5 py-5 shadow-sm mx-1 space-y-4">

            <div>
              <p className="text-[13px] font-medium text-[#1a2e1a] mb-1.5">{t.profile_nameLabel}</p>
              <input
                name="name"
                value={form.name}
                onChange={handleFormChange}
                placeholder={t.profile_namePlaceholder}
                className="w-full border-[1.5px] border-[#e0e8e0] rounded-xl px-4 py-3 text-[14px] text-[#1a2e1a] outline-none focus:border-[#3a6b3a] transition-colors placeholder:text-[#b0c4b0]"
              />
            </div>

            <div>
              <p className="text-[13px] font-medium text-[#1a2e1a] mb-1.5">{t.profile_pincodeLabel}</p>
              <div className="relative">
                <input
                  name="pin"
                  value={form.pin}
                  onChange={handleFormChange}
                  placeholder={t.profile_pincodePlaceholder}
                  inputMode="numeric"
                  maxLength={6}
                  className="w-full border-[1.5px] border-[#e0e8e0] rounded-xl px-4 py-3 text-[14px] text-[#1a2e1a] outline-none focus:border-[#3a6b3a] transition-colors placeholder:text-[#b0c4b0] pr-10"
                />
                {pincodeLoading && (
                  <Loader2 className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#2d5a2d]" />
                )}
              </div>
            </div>

            <div>
              <p className="text-[13px] font-medium text-[#1a2e1a] mb-1.5">{t.profile_stateLabel}</p>
              <input
                name="state"
                value={form.state}
                readOnly
                onChange={handleFormChange}
                placeholder={t.profile_autoFill}
                className="w-full border-[1.5px] border-[#e0e8e0] rounded-xl px-4 py-3 text-[14px] text-[#1a2e1a] outline-none focus:border-[#3a6b3a] transition-colors placeholder:text-[#b0c4b0]"
              />
            </div>

            <div>
              <p className="text-[13px] font-medium text-[#1a2e1a] mb-1.5">{t.profile_districtLabel}</p>
              <input
                name="district"
                value={form.district}
                readOnly
                onChange={handleFormChange}
                placeholder={t.profile_autoFill}
                className="w-full border-[1.5px] border-[#e0e8e0] rounded-xl px-4 py-3 text-[14px] text-[#1a2e1a] outline-none focus:border-[#3a6b3a] transition-colors placeholder:text-[#b0c4b0]"
              />
            </div>

            <div>
              <p className="text-[13px] font-medium text-[#1a2e1a] mb-1.5">{t.profile_villageLabel}</p>
              {villages.length > 0 ? (
                <select
                  name="village"
                  value={form.village}
                  onChange={handleFormChange}
                  className="w-full border-[1.5px] border-[#e0e8e0] rounded-xl px-4 py-3 text-[14px] text-[#1a2e1a] outline-none focus:border-[#3a6b3a] transition-colors bg-white appearance-none"
                >
                  {villages.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              ) : (
                <input
                  name="village"
                  value={form.village}
                  onChange={handleFormChange}
                  placeholder={t.profile_autoFill}
                  className="w-full border-[1.5px] border-[#e0e8e0] rounded-xl px-4 py-3 text-[14px] text-[#1a2e1a] outline-none focus:border-[#3a6b3a] transition-colors placeholder:text-[#b0c4b0] bg-[#f8faf8]"
                />
              )}
            </div>

            <div>
              <p className="text-[13px] font-medium text-[#1a2e1a] mb-1.5">{t.profile_acreageLabel}</p>
              <input
                name="landowned"
                value={form.landowned}
                onChange={handleFormChange}
                placeholder={t.profile_acreagePlaceholder}
                inputMode="numeric"
                className="w-full border-[1.5px] border-[#e0e8e0] rounded-xl px-4 py-3 text-[14px] text-[#1a2e1a] outline-none focus:border-[#3a6b3a] transition-colors placeholder:text-[#b0c4b0]"
              />
            </div>

          </div>

          <div
            className="rounded-2xl px-4 py-3 mx-1 flex items-start gap-2"
            style={{ backgroundColor: '#eaf3de', border: '1px solid #a8d08a' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4a7c59" strokeWidth="2" className="mt-0.5 shrink-0">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p className="text-[11px] text-[#4a7c59] leading-relaxed">{t.profile_infoNote}</p>
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-4 rounded-xl text-[15px] font-bold text-white active:scale-[0.98] transition-transform disabled:opacity-50"
            style={{ width: 'calc(100% - 8px)', marginLeft: '4px', backgroundColor: '#2d5a2d' }}
          >
            {t.profile_saveBtn}
          </button>

        

        </div>
         <div className="w-full py-3 border-t border-gray-200 mt-auto">
          <p className="text-[13px] text-black uppercase text-center tracking-wide">
            © {new Date().getFullYear()} Gencrest
          </p>
        </div>
      </div>
    </div>
  );
}